import { AppBar, Toolbar, Button, Box, Drawer, IconButton, List, ListItem, ListItemButton, ListItemText, ListItemIcon, Divider, useMediaQuery, useTheme, Typography, Avatar, Menu, MenuItem } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import LogoutIcon from "@mui/icons-material/Logout";
import { useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import themeColors from "../theme";
import Logo from "./Logo";

const baseMenuItems = [
    { label: "DASHBOARD", route: "/dashboard" },
    { label: "FUND TRACKER", route: "/purchases" },
    { label: "CREDIT TRACKER", route: "/creditTracker" },
    { label: "MARKETPLACE", route: "/marketplace" },
    { label: "VAULT", route: "/vault" },
    { label: "CLINICAL UPDATES", route: "/clinical-updates" },
    { label: "PROFILE", route: "/profile" },
    { label: "ACCOUNT SETTINGS", route: "/account" },
];

const desktopNavItems = [
    { label: "Dashboard", route: "/dashboard" },
    { label: "Fund Tracker", route: "/purchases" },
    { label: "Credit Tracker", route: "/creditTracker" },
    { label: "Marketplace", route: "/marketplace" },
    { label: "Vault", route: "/vault" },
    { label: "Clinical Updates", route: "/clinical-updates" },
];

export default function HeaderBar({ limitedNavigation = false }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { teal } = themeColors;
    const [mobileOpen, setMobileOpen] = useState(false);
    const [accountAnchor, setAccountAnchor] = useState(null);
    const { user, logout } = useAuth();
    const isAdmin = !!user?.isAdmin;
    const accountMenuOpen = Boolean(accountAnchor);
    const userInitial = (user?.email?.trim()?.charAt(0) || "").toUpperCase();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const menuItems = limitedNavigation
        ? baseMenuItems.filter(({ route }) => route === "/profile" || route === "/account")
        : [...baseMenuItems, ...(isAdmin ? [{ label: "ADMIN", route: "/admin" }] : [])];
    const navItems = limitedNavigation
        ? []
        : [...desktopNavItems, ...(isAdmin ? [{ label: "Admin", route: "/admin" }] : [])];

    const isActive = (route) => location.pathname === route || (route === "/admin" && location.pathname.startsWith("/admin"));

    const handleLogout = () => {
        logout();
        navigate("/login");
    };

    const handleAccountMenuClose = () => setAccountAnchor(null);

    const handleAccountNavigate = (route) => {
        handleAccountMenuClose();
        navigate(route);
    };

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const drawer = (
        <Box sx={{ textAlign: 'center', height: '100%', backgroundColor: teal }}>
            <Box sx={{ p: 2 }}>
                <Logo size={60} />
            </Box>
            <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.2)' }} />
            <List>
                {menuItems.map((item) => (
                    <ListItem key={item.label} disablePadding>
                        <ListItemButton 
                            onClick={() => {
                                navigate(item.route);
                                setMobileOpen(false);
                            }}
                            sx={{ 
                                textAlign: 'left',
                                px: 3,
                                color: 'white',
                                '&:hover': { backgroundColor: themeColors.darkTeal }
                            }}
                        >
                            <ListItemText primary={item.label} />
                        </ListItemButton>
                    </ListItem>
                ))}
                <ListItem disablePadding>
                    <ListItemButton 
                        onClick={() => {
                            handleLogout();
                            setMobileOpen(false);
                        }}
                        sx={{ 
                            textAlign: 'left',
                            px: 3,
                            color: 'white',
                            '&:hover': { backgroundColor: themeColors.darkTeal }
                        }}
                    >
                        <ListItemText primary="LOGOUT" />
                    </ListItemButton>
                </ListItem>
            </List>
        </Box>
    );

    return (
        <>
            <AppBar position="static" sx={{ backgroundColor: teal }}>
                <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
                    {/* Logo - Left */}
                    <Box sx={{ display: "flex", alignItems: "center", minWidth: 150 }}>
                        {isMobile && (
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                edge="start"
                                onClick={handleDrawerToggle}
                                sx={{ mr: 2 }}
                            >
                                <MenuIcon />
                            </IconButton>
                        )}
                        <Logo size={50} variant="light" />
                    </Box>

                    {/* Menu Items - Center */}
                    <Box sx={{
                        display: { xs: 'none', md: 'flex' },
                        justifyContent: 'center',
                        alignItems: 'center',
                        flex: 1
                    }}>
                        {navItems.map((item, index, arr) => (
                            <Box key={item.route} sx={{ display: 'flex', alignItems: 'center' }}>
                                <Button
                                    onClick={() => navigate(item.route)}
                                    sx={{
                                        color: 'white',
                                        textTransform: 'none',
                                        fontWeight: isActive(item.route) ? 700 : 400,
                                        fontSize: '1rem',
                                        px: 2,
                                        py: 1,
                                        borderRadius: 0,
                                        borderBottom: isActive(item.route) ? '2px solid white' : '2px solid transparent',
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                            borderBottom: '2px solid rgba(255, 255, 255, 0.5)',
                                        },
                                    }}
                                >
                                    {item.label}
                                </Button>
                                {index < arr.length - 1 && (
                                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.5)', mx: 0.5, userSelect: 'none' }}>|</Typography>
                                )}
                            </Box>
                        ))}
                    </Box>

                    {/* Account Menu - Right */}
                    <Box sx={{
                        display: { xs: 'none', md: 'flex' },
                        alignItems: 'center',
                        minWidth: 150,
                        justifyContent: 'flex-end'
                    }}>
                        <Button
                            onClick={(e) => setAccountAnchor(e.currentTarget)}
                            aria-haspopup="true"
                            aria-expanded={accountMenuOpen ? 'true' : undefined}
                            startIcon={
                                <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontSize: '0.95rem', fontWeight: 600 }}>
                                    {userInitial || <PersonOutlineIcon fontSize="small" />}
                                </Avatar>
                            }
                            endIcon={<KeyboardArrowDownIcon />}
                            sx={{
                                color: 'white',
                                textTransform: 'none',
                                fontSize: '1rem',
                                px: 1.5,
                                py: 1,
                                borderRadius: 1,
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
                            }}
                        >
                            Account
                        </Button>
                        <Menu
                            anchorEl={accountAnchor}
                            open={accountMenuOpen}
                            onClose={handleAccountMenuClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            slotProps={{ paper: { sx: { minWidth: 200, mt: 1 } } }}
                        >
                            <MenuItem onClick={() => handleAccountNavigate("/profile")}>
                                <ListItemIcon><PersonOutlineIcon fontSize="small" /></ListItemIcon>
                                <ListItemText>Profile</ListItemText>
                            </MenuItem>
                            <MenuItem onClick={() => handleAccountNavigate("/account")}>
                                <ListItemIcon><SettingsOutlinedIcon fontSize="small" /></ListItemIcon>
                                <ListItemText>Account Settings</ListItemText>
                            </MenuItem>
                            <Divider />
                            <MenuItem onClick={() => { handleAccountMenuClose(); handleLogout(); }}>
                                <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                                <ListItemText>Logout</ListItemText>
                            </MenuItem>
                        </Menu>
                    </Box>
                </Toolbar>
            </AppBar>
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{
                    keepMounted: true, // Better open performance on mobile.
                }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': { 
                        boxSizing: 'border-box', 
                        width: 280,
                        backgroundColor: teal,
                        color: 'white'
                    },
                }}
            >
                {drawer}
            </Drawer>
        </>
    );
}
